import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { Headache } from '../../types';
import './AdminHeadaches.css';

interface HeadacheWithUser extends Headache {
  user_name?: string;
  user_email?: string;
}

const AdminHeadaches: React.FC = () => {
  const [headaches, setHeadaches] = useState<HeadacheWithUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [intensityFilter, setIntensityFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadHeadaches();
  }, []);

  const loadHeadaches = async (search?: string, intensity?: string, startDate?: string, endDate?: string) => {
    try {
      setIsLoading(true);
      const params: any = { limit: 100 };
      if (search) params.search = search;
      if (intensity) params.intensity = intensity;
      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      const response = await adminAPI.getHeadaches(params);
      setHeadaches(response.data.data.headaches);
      setTotalCount(response.data.data.pagination?.total || response.data.data.headaches.length);
    } catch (err: any) {
      setError('Error al cargar registros de dolores de cabeza');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    loadHeadaches(searchTerm, intensityFilter, dateRange.startDate, dateRange.endDate);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setIntensityFilter('');
    setDateRange({ startDate: '', endDate: '' });
    loadHeadaches();
  };

  const getIntensityInfo = (intensity: string) => {
    switch (intensity) {
      case 'leve':
        return { color: '#10b981', label: 'Leve' };
      case 'moderada':
        return { color: '#f59e0b', label: 'Moderada' };
      case 'fuerte':
        return { color: '#ef4444', label: 'Fuerte' };
      default:
        return { color: '#6b7280', label: intensity };
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const exportToCSV = () => {
    const headers = ['Fecha', 'Usuario', 'Email', 'Intensidad', 'Ubicacion', 'Medicacion', 'Medicamento'];
    const rows = headaches.map(h => [
      h.date,
      h.user_name || '',
      h.user_email || '',
      h.intensity,
      h.location || '',
      h.medication_taken ? 'Si' : 'No',
      h.medication || ''
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `dolores_cabeza_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (isLoading && headaches.length === 0) {
    return <div className="admin-loading">Cargando registros...</div>;
  }

  return (
    <div className="admin-headaches">
      <div className="admin-headaches-header">
        <h1>Todos los Dolores de Cabeza</h1>
        <div className="header-stats">
          <span className="stat-badge">{totalCount} registros totales</span>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="filters-container">
        <div className="search-box">
          <input
            type="text"
            placeholder="Buscar por usuario o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="search-input"
          />
        </div>
        <div className="intensity-filter">
          <select
            value={intensityFilter}
            onChange={(e) => setIntensityFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">Todas las intensidades</option>
            <option value="leve">Leve</option>
            <option value="moderada">Moderada</option>
            <option value="fuerte">Fuerte</option>
          </select>
        </div>
        <div className="date-filters">
          <input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
            className="date-input"
          />
          <span>a</span>
          <input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
            className="date-input"
          />
        </div>
        <div className="filter-actions">
          <button onClick={handleSearch} className="btn btn-primary">Buscar</button>
          <button onClick={handleClearFilters} className="btn btn-secondary">Limpiar</button>
          <button onClick={exportToCSV} className="btn btn-success">Exportar CSV</button>
        </div>
      </div>

      {headaches.length === 0 ? (
        <div className="empty-state">
          <p>No hay registros de dolores de cabeza</p>
        </div>
      ) : (
        <div className="headaches-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Usuario</th>
                <th>Intensidad</th>
                <th>Ubicacion</th>
                <th>Medicacion</th>
                <th>Seguimiento</th>
              </tr>
            </thead>
            <tbody>
              {headaches.map((headache) => {
                const intensityInfo = getIntensityInfo(headache.intensity);
                return (
                  <tr key={headache.id}>
                    <td>{formatDate(headache.date)}</td>
                    <td>
                      <div className="user-info">
                        <span className="user-name">{headache.user_name || `Usuario ${headache.user_id}`}</span>
                        {headache.user_email && <span className="user-email">{headache.user_email}</span>}
                      </div>
                    </td>
                    <td>
                      <span
                        className="intensity-badge"
                        style={{
                          backgroundColor: `${intensityInfo.color}20`,
                          color: intensityInfo.color,
                          border: `1px solid ${intensityInfo.color}`
                        }}
                      >
                        {intensityInfo.label}
                      </span>
                    </td>
                    <td>{headache.location || '-'}</td>
                    <td>
                      {headache.medication_taken ? (
                        <div className="medication-info">
                          <span className="medication-yes">Si</span>
                          {headache.medication && <span className="medication-name">{headache.medication}</span>}
                        </div>
                      ) : (
                        <span className="medication-no">No</span>
                      )}
                    </td>
                    <td>
                      <span className={`followup-badge followup-${headache.follow_up_response}`}>
                        {headache.follow_up_response === 'improved' ? 'Mejoro' :
                         headache.follow_up_response === 'worse' ? 'Empeoro' :
                         headache.follow_up_response === 'same' ? 'Igual' :
                         headache.follow_up_response === 'pending' ? 'Pendiente' : '-'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminHeadaches;
